// QuadraticSolutionCountTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import SelectInput from "../../../../components/inputs/SelectInput";

/* === helpers: format stron równania i ukrywanie współczynnika 1 === */
function termLatex(coef, variable, isFirst) {
  if (coef === 0) return "";
  const sign = coef < 0 ? "-" : "+";
  const abs = Math.abs(coef);
  const body = variable ? (abs === 1 ? `${variable}` : `${abs}${variable}`) : `${abs}`;
  return isFirst ? (coef < 0 ? `-${body}` : `${body}`) : ` ${sign} ${body}`;
}
function sideFromCoeffs({ x2 = 0, x = 0, c = 0 }) {
  const parts = [];
  const push = (coef, variable) => {
    const isFirst = parts.length === 0;
    const t = termLatex(coef, variable, isFirst);
    if (t) parts.push(t);
  };
  push(x2, "x^2");
  push(x, "x");
  push(c, "");
  return parts.length ? parts.join("") : "0";
}

/* === opcje odpowiedzi === */
const options = [
  { value: "0", label: "brak rozwiązań" },
  { value: "1", label: "jedno rozwiązanie" },
  { value: "2", label: "dwa rozwiązania" }
];

/* === generowanie === */
export function generateTask() {
  let a, b, c, delta, expectedSolutions, originalLatex, standardLatex;

  // wylosuj, ile rozwiązań ma mieć równanie
  expectedSolutions = ["0", "1", "2"][Math.floor(Math.random() * 3)];

  while (true) {
    a = (Math.random() < 0.5 ? -1 : 1) * (Math.floor(Math.random() * 3) + 1); // ±1..±3
    b = (Math.random() < 0.5 ? -1 : 1) * (Math.floor(Math.random() * 7) + 1); // ±1..±7
    c = (Math.random() < 0.5 ? -1 : 1) * (Math.floor(Math.random() * 9) + 1); // ±1..±9
    if (a === 0) continue;

    delta = b * b - 4 * a * c;
    const actual = delta > 0 ? "2" : delta === 0 ? "1" : "0";
    if (actual !== expectedSolutions) continue;

    // z postaci standardowej zbuduj „porozrzucane” równanie (niekoniecznie =0)
    let moveA = Math.random() < 0.5;
    let moveB = Math.random() < 0.5;
    let moveC = Math.random() < 0.5;
    if (!moveA && !moveB && !moveC) moveA = true;   // prawa strona nie może być pusta
    if (moveA && moveB && moveC) moveC = false;      // lewa też nie może być pusta

    const leftCoeffs  = { x2: moveA ? 0 : a, x: moveB ? 0 : b, c: moveC ? 0 : c };
    const rightCoeffs = { x2: moveA ? -a : 0, x: moveB ? -b : 0, c: moveC ? -c : 0 };

    originalLatex = `${sideFromCoeffs(leftCoeffs)} = ${sideFromCoeffs(rightCoeffs)}`;
    standardLatex = `${sideFromCoeffs({ x2: a, x: b, c })} = 0`; // bez „1” przy x^2 i x

    return {
      type: "QuadraticSolutionCountTask",
      data: {
        a, b, c, delta,
        numSolutions: actual,
        originalLatex,
        standardLatex
      }
    };
  }
}

/* === render zadania — w jednej linijce z poleceniem === */
export function renderTask({ data }) {
  return (
    <p>
      Określ liczbę rozwiązań równania: <InlineMath math={data.originalLatex} />
    </p>
  );
}

/* === input === */
export function renderInput(_, userInput, setUserInput, disabled) {
  return (
    <SelectInput
      options={options}
      value={userInput}
      onChange={e => setUserInput(e?.target?.value ?? e)}
      placeholder="Wybierz liczbę rozwiązań"
      disabled={disabled}
    />
  );
}

/* === ocena === */
export function checkAnswer({ data }, userInput) {
  const correct = userInput === data.numSolutions;
  return { correct, expected: [data.numSolutions] };
}

/* === twoja odpowiedź === */
export function renderUserAnswer(userInput) {
  const label = options.find(o => o.value === userInput)?.label;
  return label ? <span><strong>{label}</strong></span> : <i>(brak odpowiedzi)</i>;
}

/* === poprawna odpowiedź === */
export function renderAnswer({ data }) {
  const label = options.find(o => o.value === data.numSolutions)?.label;
  return label ? <span><strong>{label}</strong></span> : null;
}

/* === wyjaśnienie === */
export function renderExplanation({ data }) {
  const { a, b, c, delta, numSolutions, originalLatex, standardLatex } = data;

  // formatowanie podstawień w delcie:
  const bPow2Latex = b < 0 ? `(${b})^2` : `${b}^2`;           // b dodatnie bez nawiasu
  const aFactor    = a < 0 ? `(${a})` : `${a}`;                // nawias tylko gdy ujemne
  const cFactor    = c < 0 ? `(${c})` : `${c}`;

  return (
    <div className="space-y-4">
      <p><strong>Krok 1:</strong> Przenosimy wszystko na jedną stronę i redukujemy:</p>
      <BlockMath math={`${originalLatex} \\Rightarrow ${standardLatex}`} />

      <p><strong>Krok 2:</strong> Współczynniki i wyróżnik kwadratowy (delta):</p>
      <BlockMath math={`a = ${a},\\quad b = ${b},\\quad c = ${c}`} />
      <p>Delta <InlineMath math={'\\Delta'} /> to <em>wyróżnik kwadratowy</em> równania:</p>
      <BlockMath math={`\\Delta = b^2 - 4ac`} />

      <p><strong>Krok 3:</strong> Liczymy deltę dla podanych współczynników:</p>
      <BlockMath math={`\\Delta = ${bPow2Latex} - 4 \\cdot ${aFactor} \\cdot ${cFactor} = ${delta}`} />

      <p><strong>Krok 4:</strong> Wnioskujemy na podstawie znaku delty:</p>
      <BlockMath math={`\\Delta > 0\\;\\Rightarrow\\; \\text{dwa rozwiązania},\\quad \\Delta = 0\\;\\Rightarrow\\; \\text{jedno},\\quad \\Delta < 0\\;\\Rightarrow\\; \\text{brak rozwiązań}`} />

      {numSolutions === "2" && (
        <BlockMath math={`\\boxed{\\Delta > 0 \\;\\Rightarrow\\; \\text{dwa rozwiązania}}`} />
      )}
      {numSolutions === "1" && (
        <BlockMath math={`\\boxed{\\Delta = 0 \\;\\Rightarrow\\; \\text{jedno rozwiązanie}}`} />
      )}
      {numSolutions === "0" && (
        <BlockMath math={`\\boxed{\\Delta < 0 \\;\\Rightarrow\\; \\text{brak rozwiązań}}`} />
      )}
    </div>
  );
}
